/**
 * Accent colours for the Tsuki theme.
 *
 * Applied at runtime to `$vuetify.theme.themes.*` — Vuetify recomputes its CSS custom properties
 * (`--v-primary-base` and friends) on assignment, and the whole stylesheet is written in terms of
 * those, so nothing needs rebuilding. Client-side only.
 *
 * Each accent carries a `primary` per theme: the dark theme needs a lighter tone to stay readable
 * on a near-black page, the light theme a darker one for contrast on white. `secondary` is the
 * shared brand tone used for fills and active states.
 */
export interface Accent {
  key: string
  /** Swatch shown in the picker, and the shared fill colour. */
  secondary: string
  darkPrimary: string
  lightPrimary: string
}

export const ACCENTS: Accent[] = [
  {key: 'violet', secondary: '#7c6cff', darkPrimary: '#9c8fff', lightPrimary: '#5643cf'},
  {key: 'blue', secondary: '#4c8dff', darkPrimary: '#7db0ff', lightPrimary: '#2563c9'},
  {key: 'teal', secondary: '#25b0a4', darkPrimary: '#5ed0c5', lightPrimary: '#12796f'},
  {key: 'green', secondary: '#4caf6a', darkPrimary: '#7fd096', lightPrimary: '#2e7d46'},
  {key: 'amber', secondary: '#e0a03a', darkPrimary: '#f0c274', lightPrimary: '#9a6a12'},
  {key: 'orange', secondary: '#f07a3c', darkPrimary: '#ffa273', lightPrimary: '#c1521a'},
  {key: 'rose', secondary: '#f0567f', darkPrimary: '#ff87a4', lightPrimary: '#c22a52'},
  {key: 'crimson', secondary: '#e04358', darkPrimary: '#f4808e', lightPrimary: '#b21f33'},
  {key: 'slate', secondary: '#7d8798', darkPrimary: '#a7b0bf', lightPrimary: '#4c5666'},
]

export const ACCENT_DEFAULT = 'violet'

export function accentByKey(key: string): Accent {
  return ACCENTS.find(a => a.key === key) ?? ACCENTS[0]
}

/**
 * Write an accent into the live Vuetify theme.
 *
 * Both themes are written, not only the active one, so switching light/dark afterwards keeps the
 * chosen accent instead of snapping back to violet.
 */
export function applyAccent(vuetify: any, key: string) {
  const accent = accentByKey(key)
  const themes = vuetify?.theme?.themes
  if (!themes) return
  themes.dark.primary = accent.darkPrimary
  themes.dark.secondary = accent.secondary
  themes.dark.accent = accent.secondary
  themes.light.primary = accent.lightPrimary
  themes.light.secondary = accent.secondary
  themes.light.accent = accent.secondary
}
